// src/stores/workflowProjectTypesStore.js
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { isSelectableProjectType } from '@/utils/selectorEligibility'
import { collection, query, orderBy } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { db } from '@/services/firebaseconfig'

export const useWorkflowProjectTypesStore = defineStore('workflowProjectTypes', () => {
  const allProjectTypes = ref([])
  const selectableProjectTypes = computed(() =>
    allProjectTypes.value.filter(isSelectableProjectType)
  )
  const isLoading = ref(false)
  const hasFetched = ref(false)
  let _unsubscribe = null
  let ready = null
  let settleReady = null

  // Phase 33B: Real-time subscription
  function subscribe(options = {}) {
    const force = options.force === true
    if (force && _unsubscribe) {
      _unsubscribe()
      _unsubscribe = null
      hasFetched.value = false
    }
    if (_unsubscribe?.active) return ready
    ready = new Promise(resolve => {
      settleReady = resolve
    })
    isLoading.value = true
    const perfLabel = `[PERF] [workflowProjectTypesStore] subscribe`
    console.time(perfLabel)
    _unsubscribe = onSnapshot(
      query(collection(db, 'workflowProjectTypes'), orderBy('displayName')),
      snap => {
        allProjectTypes.value = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        if (!hasFetched.value) console.timeEnd(perfLabel)
        hasFetched.value = true
        isLoading.value = false
        settleReady?.()
        settleReady = null
      },
      error => {
        // F6: expected for anonymous public visitors — keep §2.18 console checks signal-clean.
        if (error?.code === 'permission-denied') {
          console.debug(
            '[workflowProjectTypesStore] snapshot permission-denied (expected for public visitors)'
          )
        } else {
          console.error('[workflowProjectTypesStore] onSnapshot error:', error)
        }
        isLoading.value = false
        settleReady?.()
        settleReady = null
      }
    )
    return ready
  }

  function unsubscribeAll() {
    if (_unsubscribe) {
      _unsubscribe()
      _unsubscribe = null
    }
    hasFetched.value = false
    settleReady?.()
    settleReady = null
    ready = null
    allProjectTypes.value = []
  }

  // Backward-compatible alias
  function fetchProjectTypes(options = {}) {
    return subscribe(options)
  }

  function getProjectTypeName(projectTypeId) {
    if (!projectTypeId) return '—'
    const pt = allProjectTypes.value.find(p => p.id === projectTypeId)
    return pt?.name || pt?.displayName || '—'
  }

  function getProjectTypeById(projectTypeId) {
    if (!projectTypeId) return null
    return allProjectTypes.value.find(p => p.id === projectTypeId) || null
  }

  return {
    allProjectTypes,
    selectableProjectTypes,
    isLoading,
    hasFetched,
    fetchProjectTypes,
    subscribe,
    unsubscribeAll,
    getProjectTypeName,
    getProjectTypeById,
  }
})
